import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
	IOasisApplicationBase,
	OasisApplicationHeader,
	OasisApplicationView,
	OasisDocType,
	OasisUploadRef,
	ProgramType,
	SlipTrack,
	ApplicantStatusType,
	ApplicationStatus,
} from 'shared-resources';

import { OasisApiService } from '../../../core/api/oasis-api.service';
import { OasisConfigService } from '../../../core/cycle/oasis-config.service';
import { EducationExperienceStore } from '../sections/education-experience/education-experience.store';
import { sectionsWithAnswers, type CascadeEffect, type CascadePlan } from './substantive-changes';

/** Describes a single navigable section of the application form. */
export interface FormSection {
	key: string;
	label: string;
	route: string;
	order: number;
}

/**
 * The certification section's key.
 *
 * Named because two places have to agree about it: the section list below, and the
 * re-open handling in `hydrate` that has to un-sign it. A string literal in both
 * would drift silently — the nav would show the section incomplete while the form
 * still held an affirmed attestation, or vice versa.
 */
export const CERTIFY_SECTION_KEY = 'certifyAndSubmit';

/** All sections in their display/navigation order. */
export const APPLICATION_SECTIONS: FormSection[] = [
	{ key: 'instructions', order: 1, label: 'Instructions & Acknowledgements', route: 'instructions' },
	{ key: 'personalInformation', order: 2, label: 'Personal Information', route: 'personal-information' },
	{ key: 'citizenship', order: 3, label: 'Citizenship', route: 'citizenship' },
	{ key: 'programEligibility', order: 4, label: 'Program Eligibility', route: 'program-eligibility' },
	/**
	 * Unified Education & Experience timeline. Single source of truth for legal
	 * education, other education, and eligibility-preserving activities — the
	 * three sections it replaced were retired in the 2026 redesign.
	 */
	{ key: 'educationExperience', order: 5, label: 'Education & EPAs', route: 'education-experience' },
	{ key: 'barAdmission', order: 9, label: 'Bar Admission', route: 'bar-admission' },
	{ key: 'componentsOfInterest', order: 10, label: 'Components of Interest', route: 'components-of-interest' },
	{
		key: 'professionalAccomplishments',
		order: 12,
		label: 'Professional Accomplishments',
		route: 'professional-accomplishments',
	},
	{ key: 'honorsAndAwards', order: 13, label: 'Honors & Awards', route: 'honors-awards' },
	{ key: 'employment', order: 14, label: 'Employment', route: 'employment' },
	{ key: 'essays', order: 15, label: 'Essay', route: 'essays' },
	{ key: 'references', order: 16, label: 'References', route: 'references' },
	{ key: 'militaryService', order: 17, label: 'Military Service', route: 'military-service' },
	{ key: 'languages', order: 18, label: 'Languages', route: 'languages' },
	{ key: 'additionalInformation', order: 19, label: 'Additional Information', route: 'additional-information' },
	{ key: 'demographics', order: 20, label: 'Demographics', route: 'demographics' },
	{ key: 'supportingDocuments', order: 21, label: 'Supporting Documents', route: 'supporting-documents' },
	{ key: CERTIFY_SECTION_KEY, order: 22, label: 'Certify & Submit', route: 'certify-submit' },
];

@Injectable({ providedIn: 'root' })
export class ApplicationFormStore {
	// ── Program pathway (user can only apply to ONE) ─────────────────────────
	/** The selected program path — HP, SLIP Pathways, or SLIP Law Clerk. */
	readonly selectedProgram = signal<ProgramType | null>(null);

	/** Only set when program === 'slip'. */
	readonly selectedSlipTrack = signal<SlipTrack | null>(null);

	/**
	 * Applicant status (current law student vs. graduate). Set on the Program
	 * Eligibility page; read by later sections (e.g. Legal Education) that
	 * change wording/branching based on it. Kept on the store so sections don't
	 * have to re-derive it.
	 */
	readonly applicantStatus = signal<ApplicantStatusType | null>(null);

	/**
	 * Enrollment status for current law students (full- vs part-time). Relevant
	 * to eligibility disclosure (part-time is not EPA-eligible per program rules).
	 */
	readonly enrollmentStatus = signal<'full_time' | 'part_time' | null>(null);

	/** Expected J.D. date for current students (ISO yyyy-mm-dd). */
	readonly expectedJdDate = signal<string | null>(null);

	/** Actual J.D. completion date for graduates (ISO yyyy-mm-dd). */
	readonly jdCompletionDate = signal<string | null>(null);

	/**
	 * The single graduation date to reason about, regardless of applicant path:
	 * a graduate's completion date, otherwise a student's expected date. Lets
	 * downstream consumers (eligibility disclosure now, backend later) read one
	 * value without caring which field was filled.
	 */
	readonly effectiveGraduationDate = computed<string | null>(() =>
		this.applicantStatus() === 'law_school_graduate' ? this.jdCompletionDate() : this.expectedJdDate(),
	);

	// ── Application data ─────────────────────────────────────────────────────
	/** The full application state using the nested union types. */
	readonly formData = signal<Partial<IOasisApplicationBase>>({});

	// ── Section completion tracking ──────────────────────────────────────────
	/** Set of section keys the user has explicitly saved. */
	readonly completedSections = signal<Set<string>>(new Set());

	/**
	 * Sections whose answers were DISCARDED by a substantive change upstream (see
	 * `SUBSTANTIVE_CHANGES`). Distinct from "not completed": these were filled in
	 * and then emptied by something the applicant changed, so the screen owes them
	 * an explanation of why it looks blank. A key drops out the moment that section
	 * is saved again.
	 */
	private readonly _clearedSections = signal<Set<string>>(new Set());
	readonly clearedSections = this._clearedSections.asReadonly();

	/** Whether a section's answers were discarded and not yet re-entered. */
	isSectionCleared(sectionKey: string): boolean {
		return this._clearedSections().has(sectionKey);
	}

	/**
	 * One-shot audit marker for the next save, describing a substantive change that
	 * just discarded answers. Sent with the draft save, recorded server-side as an
	 * `answers_cleared` event, then dropped.
	 *
	 * OARM would otherwise see a submitted application quietly revert to
	 * in-progress with no stated cause. Held here rather than written into the body
	 * because it describes an ACTION, not application data.
	 */
	private readonly _pendingCascadeAudit = signal<{ changes: string[]; sections: string[] } | null>(null);

	/**
	 * The section key the applicant most recently viewed. Persisted with the
	 * draft (on `metadata.progress`) as a resume hint; the guard uses
	 * {@link resumeSection} (first-incomplete) as the actual redirect target.
	 */
	private readonly _lastVisitedSection = signal<string | null>(null);
	readonly lastVisitedSection = this._lastVisitedSection.asReadonly();

	/** Ordered section metadata array. */
	readonly sections = APPLICATION_SECTIONS;

	/**
	 * Sections in canonical order. Derived by sorting on `order` rather than
	 * trusting array position, so gating stays correct even if the `order`
	 * numbers are non-contiguous or someone reorders the array by mistake.
	 */
	private readonly orderedSections = [...APPLICATION_SECTIONS].sort((a, b) => a.order - b.order);

	/** Position of a section key within the canonical order, or -1 if unknown. */
	private orderIndex(sectionKey: string): number {
		return this.orderedSections.findIndex((s) => s.key === sectionKey);
	}

	/**
	 * Position of a section in the canonical order, for callers that need to
	 * compare two sections without knowing the section list. Unknown keys sort
	 * last so they can never become a cascade anchor by accident.
	 */
	sectionOrderIndex(sectionKey: string): number {
		const idx = this.orderIndex(sectionKey);
		return idx < 0 ? Number.MAX_SAFE_INTEGER : idx;
	}

	/**
	 * The furthest position (index in canonical order) the applicant has
	 * completed, or -1 when nothing is complete yet. Position-based so a gap in
	 * the numeric `order` values can never strand the applicant.
	 */
	readonly highestCompletedIndex = computed(() => {
		const completed = this.completedSections();
		let max = -1;
		this.orderedSections.forEach((s, i) => {
			if (completed.has(s.key)) max = Math.max(max, i);
		});
		return max;
	});

	/**
	 * Route of the section the applicant should resume at: the first section (in
	 * canonical order) they haven't completed. Falls back to the last section
	 * when everything is complete. Used as the nav guard's redirect target.
	 */
	readonly resumeSection = computed<string>(() => {
		const completed = this.completedSections();
		const firstIncomplete = this.orderedSections.find((s) => !completed.has(s.key));
		return (firstIncomplete ?? this.orderedSections[this.orderedSections.length - 1]).route;
	});

	// ── Helpers ──────────────────────────────────────────────────────────────

	/**
	 * Whether a section is accessible: it's completed, or it's the very next
	 * section after the furthest one completed. Position-based (not `order`-value
	 * based) so it advances to the next section in sequence regardless of gaps.
	 */
	isSectionAccessible(sectionKey: string): boolean {
		const idx = this.orderIndex(sectionKey);
		if (idx < 0) return false;
		if (this.completedSections().has(sectionKey)) return true;
		return idx <= this.highestCompletedIndex() + 1;
	}

	/** Whether a section is marked complete. */
	isSectionComplete(sectionKey: string): boolean {
		return this.completedSections().has(sectionKey);
	}

	/** Applicant-facing label for a section key, or the key itself if unknown. */
	sectionLabel(sectionKey: string): string {
		return this.orderedSections.find((s) => s.key === sectionKey)?.label ?? sectionKey;
	}

	/**
	 * What a cascade would actually cost the applicant, so the confirmation can
	 * name it — and so we can stay silent when it costs nothing.
	 *
	 * A first pass through Program Eligibility technically "changes" the program
	 * (from nothing to something) but there is no downstream work to lose, so no
	 * warning is warranted. Only sections that genuinely hold answers count as
	 * clearing, and only completed ones count as needing a revisit.
	 */
	previewCascade(plan: CascadePlan): CascadeEffect {
		const data = this.formData() as Record<string, unknown>;
		const clearing = sectionsWithAnswers(plan.clears, data);

		const anchorIdx = this.orderIndex(plan.anchorSection);
		const completed = this.completedSections();
		const revisiting =
			anchorIdx < 0
				? []
				: this.orderedSections
						.slice(anchorIdx + 1)
						.filter((s) => completed.has(s.key))
						.map((s) => s.key);

		return { clearing, revisiting, isNoop: clearing.length === 0 && revisiting.length === 0 };
	}

	/**
	 * Whether saving would re-open an already-submitted application. The draft save
	 * pulls `submitted`/`resubmitted`/`returned` back to `in_progress` server-side,
	 * which means the applicant has to certify and submit again — worth saying out
	 * loud before they commit to a change.
	 */
	saveWouldReopenSubmission(): boolean {
		const status = this._currentHeader()?.status;
		return status !== undefined && status !== 'in_progress';
	}

	/**
	 * Mark a section complete and merge its data into formData.
	 *
	 * OARM review flags are NOT tracked here. The API derives them from the stored
	 * body on every save (draft and submit alike), so they always match the data
	 * and retract when the applicant corrects what raised them. The applicant app
	 * has no applicant-facing use for a flag and does not send any.
	 */
	saveSection(sectionKey: string, data: Partial<IOasisApplicationBase>): void {
		// Merge data into the in-memory working copy. Server persistence happens
		// separately via `saveProgress()` (draft) / `submit()` — the base section
		// component calls `saveProgress()` right after this on Save & Continue.
		this.formData.update((current) => ({ ...current, ...data }));

		// Mark complete
		this.completedSections.update((set) => {
			const next = new Set(set);
			next.add(sectionKey);
			return next;
		});

		// Saving is what resolves a cleared section: the applicant has now supplied
		// answers under the new context, so the "this was cleared" notice retires.
		this._clearedSections.update((set) => {
			if (!set.has(sectionKey)) return set;
			const next = new Set(set);
			next.delete(sectionKey);
			return next;
		});
	}

	/**
	 * Apply a substantive change: discard the answers it invalidates and force the
	 * applicant to walk the rest of the form again.
	 *
	 * Called by the section that owns the changed field, from its save path, AFTER
	 * the applicant has confirmed. Deliberately does NOT persist — the caller's
	 * normal `saveProgress()` writes the result, so the clear and the new answer
	 * land in one save rather than two.
	 *
	 * Clearing removes the section's slice from the working copy. A section's body
	 * key is its section key by convention (`componentsOfInterest` owns
	 * `body.componentsOfInterest`), which is what makes this generic.
	 */
	applySubstantiveChange(plan: CascadePlan): void {
		// 1. Discard the invalidated answers — but only mark the sections that had
		//    answers to lose. On a first pass through the form the applicant is
		//    answering these questions for the first time, so a plan can name
		//    Components of Interest while it is still empty. Marking it then would
		//    show "your previous answers were cleared" to someone who has never
		//    been to the page.
		const cleared = sectionsWithAnswers(plan.clears, this.formData() as Record<string, unknown>);
		if (cleared.length > 0) {
			this.formData.update((current) => {
				const next = { ...current } as Record<string, unknown>;
				for (const sectionKey of cleared) {
					delete next[sectionKey];
				}
				return next as Partial<IOasisApplicationBase>;
			});
			this._clearedSections.update((set) => {
				const next = new Set(set);
				for (const sectionKey of cleared) next.add(sectionKey);
				return next;
			});
		}

		// 2. Re-lock everything after the earliest changed section. Broader than the
		//    clear on purpose — an answer that survived may still be wrong now, and
		//    partial invalidation would not hold anyway (see `isSectionAccessible`).
		this.invalidateCompletionAfter(plan.anchorSection);

		// 3. Leave a marker for the next save so OARM gets an audit entry naming the
		//    change and what it cost.
		this._pendingCascadeAudit.set({
			changes: plan.changes.map((c) => c.key),
			sections: cleared,
		});
	}

	/** Record the section the applicant is currently on (persisted as a resume hint). */
	setLastVisited(sectionKey: string): void {
		this._lastVisitedSection.set(sectionKey);
	}

	/**
	 * Drop completion for every section AFTER `fromKey` (in canonical order),
	 * forcing the applicant to revisit them. This is the mechanism behind
	 * reset-on-substantive-change: when a change invalidates downstream answers
	 * (e.g. switching program on Program Eligibility), the owning section calls
	 * {@link applySubstantiveChange}, which lands here so the nav guard re-locks
	 * everything past the change. The section that owns the change keeps its own
	 * completion (it's the one being saved).
	 *
	 * Always takes EVERYTHING downstream, never a subset: `isSectionAccessible`
	 * gates on the furthest completed section, so a surviving later completion
	 * would leave the whole form reachable.
	 */
	invalidateCompletionAfter(fromKey: string): void {
		const fromIdx = this.orderIndex(fromKey);
		if (fromIdx < 0) return;
		this.completedSections.update((set) => {
			const next = new Set(set);
			this.orderedSections.forEach((s, i) => {
				if (i > fromIdx) next.delete(s.key);
			});
			return next;
		});
	}

	/** Set the program pathway. User selects this once. */
	setProgram(program: ProgramType, slipTrack?: SlipTrack): void {
		this.selectedProgram.set(program);
		this.selectedSlipTrack.set(slipTrack ?? null);
	}

	/** Set the applicant status (current student vs. graduate). */
	setApplicantStatus(status: ApplicantStatusType): void {
		this.applicantStatus.set(status);
	}

	/**
	 * Set the eligibility-relevant timing fields from Program Eligibility. Kept
	 * on the store so later sections (e.g. the Education & Experience timeline)
	 * can gate what they disclose without re-collecting these.
	 */
	setEligibilityTiming(input: {
		enrollmentStatus?: 'full_time' | 'part_time' | null;
		expectedJdDate?: string | null;
		jdCompletionDate?: string | null;
	}): void {
		if (input.enrollmentStatus !== undefined) this.enrollmentStatus.set(input.enrollmentStatus);
		if (input.expectedJdDate !== undefined) this.expectedJdDate.set(input.expectedJdDate);
		if (input.jdCompletionDate !== undefined) this.jdCompletionDate.set(input.jdCompletionDate);
	}

	/** Get the next section route after the given key, or null if last. */
	getNextRoute(currentKey: string): string | null {
		const idx = this.orderIndex(currentKey);
		if (idx < 0 || idx >= this.orderedSections.length - 1) return null;
		return this.orderedSections[idx + 1].route;
	}

	/** Get the previous section route before the given key, or null if first. */
	getPreviousRoute(currentKey: string): string | null {
		const idx = this.orderIndex(currentKey);
		if (idx <= 0) return null;
		return this.orderedSections[idx - 1].route;
	}

	/** Reset the full store. */
	reset(): void {
		this.formData.set({});
		this.completedSections.set(new Set());
		this._clearedSections.set(new Set());
		this._pendingCascadeAudit.set(null);
		this._lastVisitedSection.set(null);
		this.selectedProgram.set(null);
		this.selectedSlipTrack.set(null);
		this.applicantStatus.set(null);
		this.enrollmentStatus.set(null);
		this.expectedJdDate.set(null);
		this.jdCompletionDate.set(null);
		this.timeline.hydrate(undefined);
		this._currentHeader.set(null);
		this._canEdit.set(null);
		this._loaded.set(false);
	}

	// ── Backend wiring (added for OASIS-external rollout) ────────────────────

	private readonly api = inject(OasisApiService);
	private readonly cycleConfig = inject(OasisConfigService);

	/**
	 * The Education & Experience timeline's working copy. Held here so the load
	 * path can hydrate it and the submit path can read it as the authoritative
	 * source, without either having to go through the section component.
	 */
	private readonly timeline = inject(EducationExperienceStore);

	/** Header of the loaded current-cycle application (null when none yet). */
	private readonly _currentHeader = signal<OasisApplicationHeader | null>(null);
	readonly currentHeader = this._currentHeader.asReadonly();

	/**
	 * True while the applicant is editing an application they had ALREADY submitted
	 * and has not submitted it again yet.
	 *
	 * `submittedAt` is stamped on the first submit and never cleared, so
	 * "`in_progress` but has a `submittedAt`" is exactly the re-opened state — it
	 * cannot be confused with a first-time draft, which has no `submittedAt`, and it
	 * clears itself the moment the status moves to `submitted`/`resubmitted`.
	 *
	 * This drives a persistent reminder, because the consequence of walking away here
	 * is silent and total: a re-opened application sits in `in_progress`, and nothing
	 * reviews an `in_progress` application. The applicant would lose consideration
	 * entirely while believing they had already applied.
	 */
	readonly awaitingResubmission = computed<boolean>(() => {
		const header = this._currentHeader();
		return header?.status === 'in_progress' && !!header.submittedAt;
	});

	/**
	 * A started-but-unfinished HP → SLIP Law Clerk transfer.
	 *
	 * Driven by the SERVER's marker rather than by how the applicant arrived, which is
	 * what makes it durable: someone who confirms the switch, closes the tab, and comes
	 * back tomorrow still gets told what they started and what is left to do. A cue that
	 * depended on the navigation would be gone by then.
	 *
	 * Narrower than {@link awaitingResubmission} and true at the same time, so the
	 * banner picks the transfer wording when this holds and falls back to the generic
	 * reminder otherwise.
	 *
	 * ## Why the timestamp comparison, and not just `in_progress` + the marker
	 *
	 * `programChangeRequestedAt` is stamped once and NEVER cleared, deliberately — a
	 * completed transfer still carries it. So the marker alone cannot mean "unfinished",
	 * and `status === 'in_progress'` was doing that job: a finished transfer had moved on
	 * to `resubmitted`.
	 *
	 * That proxy broke, because `in_progress` is reachable AGAIN after the transfer is
	 * done. Any applicant who transferred, resubmitted, and then re-opened to edit
	 * before the deadline was told to "finish switching to SLIP Law Clerk" — a switch
	 * they had already completed. Worse, this branch wins the `@else if` in the template,
	 * so it SUPPRESSED the generic "your application is not submitted, you must certify
	 * and submit again" reminder — replacing a true and urgent message with a false one.
	 * Undoing a withdrawal reaches the same state (found in UAT, Sept 2026) but the
	 * ordinary edit-after-transfer path had it first.
	 *
	 * So the real question is whether they have submitted SINCE requesting the transfer.
	 * Comparing against the latest submission answers exactly that, using timestamps the
	 * header already carries. Sorting both rather than assuming `resubmittedAt` is later
	 * than `submittedAt` — the assumption holds today, and costs nothing to not rely on.
	 *
	 * No submission at all keeps it TRUE, which is the safe direction: a transfer can
	 * only start from a status that has been submitted, so this is unreachable in
	 * practice, and showing the more specific instruction is the better failure.
	 */
	readonly transferInProgress = computed<boolean>(() => {
		const header = this._currentHeader();
		if (header?.status !== 'in_progress' || !header.programChangeRequestedAt) return false;
		const lastSubmission = [header.submittedAt, header.resubmittedAt]
			.filter((t): t is string => !!t)
			.sort()
			.at(-1);
		return !lastSubmission || header.programChangeRequestedAt > lastSubmission;
	});

	/**
	 * The transfer deadline, for the banner.
	 *
	 * Served by the cycle-config endpoint, so it is the same value the server enforces
	 * the window against — never derived locally. `null` when the cycle configures no
	 * transfer window, in which case the banner omits the date rather than inventing one.
	 */
	readonly programChangeDeadline = computed<string | null>(() => this.cycleConfig.programChangeDeadline() ?? null);

	/**
	 * Whether the server said this applicant may still save — `null` when NOT KNOWN.
	 *
	 * Three states, not two, and that is the whole point. `canEdit` is the only correct
	 * answer to "may this applicant edit this application": a requested HP→SLIP-Law-Clerk
	 * transfer re-opens editing AFTER `submissionDeadline`, so the answer depends on
	 * per-application state rather than the calendar. `OasisPublicConfig` says the same —
	 * its dates are for display and disclosure, never authorization.
	 *
	 * ## Why `null` exists
	 *
	 * This was a `boolean` defaulting to false, and that shipped a bug. `loadFromServer`
	 * only obtains flags when it FINDS an application, so a first-time applicant loaded
	 * with none, created one by saving, and then read `false` — "the server refused" —
	 * when the truth was "nobody ever asked". The route guard duly threw them out of the
	 * form immediately after a successful save.
	 *
	 * Collapsing unknown into false is unsafe in a way collapsing it into true is not,
	 * because the server is the real gate. So callers must handle `null` explicitly, and
	 * the type makes them.
	 *
	 * Snapshotted, so it can go stale if the deadline passes mid-session. Bounded and
	 * accepted: the next save is refused with `WINDOW_CLOSED` and the form now reports
	 * that accurately instead of blaming the network.
	 */
	private readonly _canEdit = signal<boolean | null>(null);
	readonly serverCanEdit = computed<boolean | null>(() => this._canEdit());

	/** Whether `loadFromServer` has completed successfully at least once. */
	private readonly _loaded = signal(false);
	readonly loaded = this._loaded.asReadonly();

	/**
	 * Hydrate the store from the backend.
	 *
	 * - With an `applicationId`, fetches that specific application.
	 * - Without one, looks for an in-progress current-cycle application
	 *   (matching the served cycle year) in the user's list.
	 *
	 * Safe to call multiple times; idempotent after the first success.
	 */
	async loadFromServer(applicationId?: string): Promise<void> {
		if (this._loaded() && !applicationId) return;

		if (applicationId) {
			const view = await firstValueFrom(this.api.getApplication(applicationId));
			if (view) {
				this.hydrate(view.body, view.header, view.flags);
			}
			return;
		}

		const page = await firstValueFrom(this.api.listMyApplications());
		const cycle = this.cycleConfig.cycleYear();
		const match = (page.items ?? []).find((h) => h.cycleYear === cycle);
		if (!match) {
			this._loaded.set(true);
			return;
		}
		const view = await firstValueFrom(this.api.getApplication(match.applicationId));
		if (view) {
			this.hydrate(view.body, view.header, view.flags);
		}
	}

	/**
	 * Submit the in-memory body to the backend. The server treats this as
	 * either a first submission or a resubmission depending on the cycle
	 * window and applicant eligibility — we don't decide that client-side. This
	 * is the ONLY path that triggers server-side review-flag logic.
	 */
	async submit(): Promise<OasisApplicationHeader> {
		const body = this.composeBody('certified_submitted');
		const result = await firstValueFrom(this.api.saveApplication(body));
		this._currentHeader.set(result.header);
		// The server accepted the write, so it authorized the edit. Recording that is
		// not a guess — it is the observation. Without it, an applicant who creates their
		// application in this session leaves `_canEdit` unknown for the rest of it.
		this._canEdit.set(true);
		this._loaded.set(true);
		return result.header;
	}

	/**
	 * Persist the current in-memory application as an `in_progress` draft. Fired
	 * on every "Save & Continue". Hits the `?intent=draft` path, which stores the
	 * body verbatim and runs NONE of the submit-time review logic. Throws on
	 * failure so the caller can keep the applicant on the page to retry.
	 */
	async saveProgress(): Promise<OasisApplicationHeader> {
		const body = this.composeBody('in_progress');
		const result = await firstValueFrom(this.api.saveApplication(body, { draft: true }));
		this._currentHeader.set(result.header);
		// See `submit()`: an accepted write IS the server's answer to "may they edit".
		// This is the path the guard bug ran through — a first-time applicant saved
		// successfully, `_canEdit` stayed unknown, the old boolean read it as `false`, and
		// the guard ejected them to the dashboard between the save and the next section.
		this._canEdit.set(true);
		this._loaded.set(true);
		/*
		 * The save is what re-opens the application, so the save is what invalidates the
		 * certification — even when the applicant changed nothing. Clicking Save & Continue
		 * moved the application out of OARM's hands and back to a draft; re-certifying is
		 * how it gets back, and there is no version of that which is merely cosmetic.
		 *
		 * Unconditional rather than "only on the transition": if they certify and then save
		 * some other section afterwards, that attestation is stale again and has to go.
		 */
		if (this.certificationIsStale(result.header)) this.clearStaleCertification();
		// Cleared only on success, so a failed save leaves the marker for the retry
		// rather than losing the audit entry.
		this._pendingCascadeAudit.set(null);
		return result.header;
	}

	/**
	 * Whether the stored certification no longer describes the application.
	 *
	 * True only once a save has pulled a previously-submitted application back to a
	 * draft. `submittedAt` is stamped on the first submit and never cleared, so it is
	 * what separates a re-opened application from a first-time draft.
	 */
	private certificationIsStale(header: OasisApplicationHeader): boolean {
		return header.status === 'in_progress' && !!header.submittedAt;
	}

	/**
	 * Un-sign the certification: drop the section's completion tick AND discard the
	 * stored attestations.
	 *
	 * Both halves are required, and doing one without the other is worse than doing
	 * neither. `completedSections` is what the nav's ticks read, so leaving it says
	 * there is nothing left to do. The four attestations are restored into the
	 * section's form controls straight from the body, so leaving them affirmed means
	 * the applicant can "re-certify" by clicking Submit without re-reading a word.
	 *
	 * Nothing needs to persist this. Staleness is re-derived from the header on every
	 * load, so a save that writes the old progress is corrected the next time the form
	 * opens rather than leaving a wrong tick behind.
	 */
	private clearStaleCertification(): void {
		this.completedSections.update((set) => {
			const next = new Set(set);
			next.delete(CERTIFY_SECTION_KEY);
			return next;
		});
		this.formData.update((data) => {
			const next = { ...data };
			delete next.certifyAndSubmit;
			return next;
		});
	}

	/**
	 * Request a presigned URL, push the file to S3, and return the
	 * `OasisUploadRef` to embed in the application body. Requires the
	 * application to already exist on the server (call `submit()` first
	 * for brand-new applications, or rely on an existing header).
	 */
	async uploadFile(file: File, docType: OasisDocType): Promise<OasisUploadRef> {
		const header = this._currentHeader();
		if (!header) {
			throw new Error('Cannot upload before the application exists. Save the application first.');
		}
		return await firstValueFrom(this.api.uploadFile(header.applicationId, file, docType));
	}

	/** Internal: load server data into the signals. */
	private hydrate(body: IOasisApplicationBase, header: OasisApplicationHeader, flags?: OasisApplicationView['flags']): void {
		this.formData.set(body);
		this._currentHeader.set(header);
		// Only when the caller actually has the server's answer. Absent flags leave this
		// UNKNOWN rather than false — see the note on `_canEdit`.
		if (flags) this._canEdit.set(flags.canEdit);
		if (header.program) this.selectedProgram.set(header.program);
		if (header.slipTrack) this.selectedSlipTrack.set(header.slipTrack);
		// Restore the Education & Experience timeline's working copy. Its store is
		// root-scoped and is also read by the Employment screen, so it has to be
		// filled on load rather than when the section is first opened.
		this.timeline.hydrate(body.educationExperience);
		// Restore applicant status + eligibility timing from the persisted
		// eligibility block, if present.
		const elig = (
			body as {
				programEligibility?: {
					applicantStatus?: ApplicantStatusType;
					lawSchoolEnrollmentStatus?: 'full_time' | 'part_time';
					expectedJDDate?: string;
					jdCompletionDate?: string;
				};
			}
		).programEligibility;
		if (elig?.applicantStatus) this.applicantStatus.set(elig.applicantStatus);
		if (elig?.lawSchoolEnrollmentStatus) this.enrollmentStatus.set(elig.lawSchoolEnrollmentStatus);
		if (elig?.expectedJDDate) this.expectedJdDate.set(elig.expectedJDDate);
		if (elig?.jdCompletionDate) this.jdCompletionDate.set(elig.jdCompletionDate);
		// Review flags are deliberately NOT restored into client state. The API is
		// their sole author and re-derives them from the body on every save, so
		// holding a copy here could only go stale — which it previously did, in a
		// bucket nothing ever cleared.
		// Restore section-completion + resume hint from persisted progress. For a
		// live `in_progress` draft this is the real, saved progress. For anything
		// already past the draft stage (submitted/returned/etc.), or a legacy body
		// written before progress tracking existed, treat every section as complete
		// so navigation is fully unlocked (gating only matters mid-draft).
		const progress = body.metadata?.progress;
		if (progress?.completedSections?.length) {
			this.completedSections.set(new Set(progress.completedSections));
		} else if (header.status !== 'in_progress') {
			this.completedSections.set(new Set(APPLICATION_SECTIONS.map((s) => s.key)));
		} else {
			this.completedSections.set(new Set());
		}
		/*
		 * Un-sign the certification when this application was re-opened in an EARLIER
		 * session — a refresh mid-edit. The equivalent for the current session lives in
		 * `saveProgress`, because a draft save does not re-hydrate.
		 *
		 * Gated on `in_progress` + `submittedAt`, NOT on "has been submitted". Opening a
		 * submitted application changes nothing: the applicant may be reading OARM's
		 * comments and deciding to come back later, or looking and leaving it alone. Until
		 * they save, their application stays submitted and their certification stays valid.
		 *
		 * That is deliberate beyond politeness. If merely opening the form started the
		 * resubmission, an applicant who reads what OARM asked for and walks away would be
		 * recorded as having begun a resubmission they never began, and the drop-off point
		 * we want to measure would be unmeasurable.
		 */
		if (this.certificationIsStale(header)) this.clearStaleCertification();
		if (progress?.lastVisitedSection) this._lastVisitedSection.set(progress.lastVisitedSection);
		// Sections cleared by a substantive change stay cleared across a reload, so
		// the applicant still sees why the page is blank when they come back to it.
		this._clearedSections.set(new Set(progress?.clearedSections ?? []));
		this._loaded.set(true);
	}

	/**
	 * Compose the full `IOasisApplicationBase` payload from in-memory pieces,
	 * stamping the cycle year, application status, and the applicant's progress
	 * (completed sections + last-visited hint). Used for both draft saves
	 * (`in_progress`) and final submit (`certified_submitted`).
	 *
	 * `reviewFlags` is deliberately absent — the API derives it from this body on
	 * every save and is its sole author.
	 */
	private composeBody(applicationStatus: ApplicationStatus): IOasisApplicationBase {
		const data = this.formData() as IOasisApplicationBase;
		const program = this.selectedProgram();
		const slipTrack = this.selectedSlipTrack();
		const cycleYear = data.metadata?.cycleYear ?? this.cycleConfig.cycleYear();
		const lastVisited = this._lastVisitedSection();
		const cleared = Array.from(this._clearedSections());

		const merged: IOasisApplicationBase = {
			...data,
			metadata: {
				...(data.metadata ?? {}),
				cycleYear,
				lastModified: new Date().toISOString(),
				applicationStatus,
				progress: {
					completedSections: Array.from(this.completedSections()),
					...(lastVisited ? { lastVisitedSection: lastVisited } : {}),
					...(cleared.length ? { clearedSections: cleared } : {}),
				},
			},
			// The timeline store is the live source of truth for Education &
			// Experience, so stamp it here rather than relying on the section having
			// been saved.
			educationExperience: this.timeline.toPersistable(),
		} as IOasisApplicationBase;

		if (program) (merged as { program?: ProgramType }).program = program;
		if (slipTrack) (merged as { slipTrack?: SlipTrack }).slipTrack = slipTrack;

		// Transient audit marker, stripped by the server before the body is stored.
		// Not part of the application — it describes the action that produced it.
		const audit = this._pendingCascadeAudit();
		if (audit) (merged as { _answersCleared?: unknown })._answersCleared = audit;

		return merged;
	}
}
